import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight, Heart, Phone, Mail, MapPin, Instagram, Facebook, ChevronRight, ChevronLeft, Sparkles, Check, FileText, Star, Calendar, Clock, Tag } from "lucide-react";
import { Toaster, toast } from "sonner";
import { BLOG_POSTS, getBlogBySlug } from "./blogData";
import { INFO_PAGES } from "./infoPages";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_iragai-gallery/artifacts/zmnptker_IRAGAI_LOGO-removebg-preview.png";

// Per-route SEO hook: updates document title, meta description, canonical, and OG tags
const usePageSEO = ({ title, description, path, image, imageAlt, type = "website" }) => {
  useEffect(() => {
    if (title) document.title = title;
    const setMeta = (selector, attr, value) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement(selector.startsWith('meta[property') ? 'meta' : (selector.startsWith('link') ? 'link' : 'meta'));
        if (selector.startsWith('meta[property')) {
          const m = selector.match(/property="([^"]+)"/);
          if (m) el.setAttribute('property', m[1]);
        } else if (selector.startsWith('meta[name')) {
          const m = selector.match(/name="([^"]+)"/);
          if (m) el.setAttribute('name', m[1]);
        } else if (selector.startsWith('link[rel')) {
          const m = selector.match(/rel="([^"]+)"/);
          if (m) el.setAttribute('rel', m[1]);
        }
        document.head.appendChild(el);
      }
      el.setAttribute(attr, value);
    };
    if (description) {
      setMeta('meta[name="description"]', 'content', description);
      setMeta('meta[property="og:description"]', 'content', description);
      setMeta('meta[name="twitter:description"]', 'content', description);
    }
    if (title) {
      setMeta('meta[property="og:title"]', 'content', title);
      setMeta('meta[name="twitter:title"]', 'content', title);
    }
    if (path) {
      const url = `https://www.iragai.in${path}`;
      setMeta('link[rel="canonical"]', 'href', url);
      setMeta('meta[property="og:url"]', 'content', url);
    }
    if (image) {
      setMeta('meta[property="og:image"]', 'content', image);
      setMeta('meta[name="twitter:image"]', 'content', image);
      if (imageAlt) {
        setMeta('meta[property="og:image:alt"]', 'content', imageAlt);
        setMeta('meta[name="twitter:image:alt"]', 'content', imageAlt);
      }
    }
    setMeta('meta[property="og:type"]', 'content', type);
  }, [title, description, path, image, imageAlt, type]);
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/collection", label: "Collection" },
    { to: "/blog", label: "Journal" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
  ];

  return (
    <header 
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled ? "glass-header py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <Link to="/" data-testid="logo" className="flex items-center gap-3">
          <img 
            src={LOGO_URL} 
            alt="Iragai - Sacred Pichwai Art Gallery Logo" 
            className="h-12 w-auto"
          />
          <span className="font-heading text-2xl font-light tracking-tight text-iragai-dark">
            Iragai
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-10">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={`nav-${link.label.toLowerCase()}`}
              className="font-body text-sm font-medium text-iragai-text hover:text-iragai-gold transition-colors duration-300 tracking-wide uppercase"
            >
              {link.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <Link
            to="/contact"
            data-testid="header-enquire"
            className="hidden sm:inline-flex items-center gap-2 bg-iragai-dark text-beige-50 px-5 py-2.5 font-body text-xs uppercase tracking-wider hover:bg-iragai-gold transition-colors duration-300"
          >
            Enquire Now
          </Link>
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-beige-50 border-t border-iragai-dark/5"
          >
            <nav className="flex flex-col p-6 gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className="font-body text-lg text-iragai-dark py-2 border-b border-iragai-dark/5"
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={() => setMobileMenuOpen(false)}
                className="bg-iragai-dark text-beige-50 py-3 text-center font-body text-sm uppercase tracking-wider mt-2"
              >
                Enquire Now
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

// Footer Component
const Footer = () => (
  <footer data-testid="footer" className="bg-iragai-dark text-beige-50 py-16">
    <div className="max-w-7xl mx-auto px-6">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-12">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-4">
            <img 
              src={LOGO_URL} 
              alt="Iragai — Handmade Pichwai Embroidery Artwork" 
              className="h-14 w-auto brightness-0 invert"
            />
            <span className="font-heading text-3xl font-light">Iragai</span>
          </div>
          <p className="font-body text-beige-200 text-sm leading-relaxed max-w-md">
            Preserving the sacred art of Pichwai through handmade embroidery, hand-stitched in Nathdwara using zardozi, fine beadwork, gold thread, sequins, and pearls on silk.
          </p>
        </div>
        <div>
          <h4 className="font-heading text-lg mb-4">Explore</h4>
          <nav className="flex flex-col gap-2">
            <Link to="/collection" className="text-beige-200 hover:text-iragai-gold transition-colors text-sm">Collection</Link>
            <Link to="/custom-orders" className="text-beige-200 hover:text-iragai-gold transition-colors text-sm">Custom Orders</Link>
            <Link to="/blog" className="text-beige-200 hover:text-iragai-gold transition-colors text-sm">Journal</Link>
            <Link to="/about" className="text-beige-200 hover:text-iragai-gold transition-colors text-sm">About Us</Link>
            <Link to="/contact" className="text-beige-200 hover:text-iragai-gold transition-colors text-sm">Contact</Link>
          </nav>
        </div>
        <div>
          <h4 className="font-heading text-lg mb-4">Help</h4>
          <nav className="flex flex-col gap-2">
            <Link to="/faq" className="text-beige-200 hover:text-iragai-gold transition-colors text-sm">FAQ</Link>
            <Link to="/shipping" className="text-beige-200 hover:text-iragai-gold transition-colors text-sm">Shipping &amp; Delivery</Link>
            <Link to="/refund-policy" className="text-beige-200 hover:text-iragai-gold transition-colors text-sm">Returns &amp; Refunds</Link>
            <Link to="/care-guide" className="text-beige-200 hover:text-iragai-gold transition-colors text-sm">Care Guide</Link>
          </nav>
        </div>
        <div>
          <h4 className="font-heading text-lg mb-4">Connect</h4>
          <div className="flex gap-4 mb-4">
            <a href="https://www.instagram.com/iragai.art" target="_blank" rel="noopener noreferrer external" className="text-beige-200 hover:text-iragai-gold transition-colors" aria-label="Follow Iragai on Instagram" onClick={(e) => { e.preventDefault(); window.open('https://www.instagram.com/iragai.art', '_blank', 'noopener,noreferrer'); }}>
              <Instagram className="w-5 h-5" />
            </a>
            <a href="https://www.facebook.com/IragaiArt" target="_blank" rel="noopener noreferrer external" className="text-beige-200 hover:text-iragai-gold transition-colors" aria-label="Follow Iragai on Facebook" onClick={(e) => { e.preventDefault(); window.open('https://www.facebook.com/IragaiArt', '_blank', 'noopener,noreferrer'); }}>
              <Facebook className="w-5 h-5" />
            </a>
          </div>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 border border-iragai-gold text-beige-50 px-4 py-2 font-body text-xs uppercase tracking-wider hover:bg-iragai-gold transition-colors"
          >
            Enquire Now
          </Link>
        </div>
      </div>
      <div className="border-t border-beige-50/10 mt-12 pt-8 text-center">
        <p className="text-beige-200 text-sm">&copy; 2025 Iragai. All rights reserved.</p>
      </div>
    </div>
  </footer>
);

// Image Gallery Component for Artwork Detail
const ImageGallery = ({ images, title }) => {
  const [selectedIndex, setSelectedIndex] = useState(0);

  const nextImage = () => {
    setSelectedIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setSelectedIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  if (!images || images.length === 0) return null;

  return (
    <div className="space-y-4">
      {/* Main Image */}
      <div className="relative bg-beige-100 overflow-hidden group">
        <AnimatePresence mode="wait">
          <motion.img
            key={selectedIndex}
            src={images[selectedIndex]}
            alt={`${title} - Handcrafted Pichwai Art View ${selectedIndex + 1}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full h-auto max-h-[70vh] object-contain"
          />
        </AnimatePresence>
        
        {/* Navigation Arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={prevImage}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-beige-50/90 backdrop-blur-sm p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-iragai-gold hover:text-beige-50"
              data-testid="gallery-prev"
              aria-label="Previous image"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={nextImage}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-beige-50/90 backdrop-blur-sm p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-iragai-gold hover:text-beige-50"
              data-testid="gallery-next"
              aria-label="Next image"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}

        {/* Image Counter */}
        {images.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-beige-50/90 backdrop-blur-sm px-3 py-1 text-sm font-body">
            {selectedIndex + 1} / {images.length}
          </div>
        )}
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
          {images.map((img, index) => (
            <button
              key={index}
              onClick={() => setSelectedIndex(index)}
              className={`flex-shrink-0 w-20 h-20 overflow-hidden border-2 transition-all duration-300 ${
                selectedIndex === index 
                  ? 'border-iragai-gold' 
                  : 'border-transparent hover:border-iragai-gold/50'
              }`}
              data-testid={`thumbnail-${index}`}
              aria-label={`View ${title} image ${index + 1}`}
            >
              <img
                src={img}
                alt={`${title} - Traditional Indian Art Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Customer Reviews Data
const CUSTOMER_REVIEWS = [
  {
    name: "Priya Shah",
    city: "Mumbai",
    rating: 5,
    text: "Honestly, the photos don't do justice to the actual piece. The detailing on the Shrinathji we got is unreal, the pearl work, the lotus motifs, everything looks like it was made with so much love. Our drawing room finally feels complete."
  },
  {
    name: "Rajesh Mehta",
    city: "Ahmedabad",
    rating: 5,
    text: "I was looking for a proper traditional Pichwai for our new home in Bopal for almost a year. Came across Iragai and decided to enquire. The team was patient, answered every question, even shared close-up videos. Very happy with the purchase."
  },
  {
    name: "Ananya Iyer",
    city: "Bangalore",
    rating: 5,
    text: "Gifted a Pichwai to my mother on her 60th birthday and she literally had tears in her eyes. The framing was beautiful and it arrived without a single scratch. Thank you Iragai team, you made this very special."
  },
  {
    name: "Vikram Desai",
    city: "Surat",
    rating: 5,
    text: "What I appreciated most is that there's no pushy sales talk. You enquire, they explain the artwork, the artisans, the process, and you decide. Felt more like talking to a gallery curator than a shop. Will surely buy again."
  },
  {
    name: "Meera Joshi",
    city: "Pune",
    rating: 5,
    text: "The peacock Pichwai we ordered is stunning. The colours are so vibrant in natural light. Honestly worth every rupee. My in-laws keep asking where we got it from!"
  },
  {
    name: "Arjun Kapoor",
    city: "Delhi",
    rating: 5,
    text: "Packaging was top-notch, wooden crate, bubble wrap, the works. Came all the way to Delhi from Mumbai in perfect condition. Even the certificate of authenticity was a nice touch. Very professional setup."
  },
  {
    name: "Kavita Patel",
    city: "Vadodara",
    rating: 4,
    text: "Lovely artwork and beautiful craftsmanship. Delivery took a little longer than I expected but the team kept me updated throughout. Once it arrived, all the wait was worth it. Will recommend to family."
  },
  {
    name: "Siddharth Rao",
    city: "Hyderabad",
    rating: 5,
    text: "Bought a Shrinathji as an anniversary gift for my wife and she has not stopped talking about it. The gold detailing catches the light so beautifully in the evenings. Absolutely a heirloom piece."
  },
  {
    name: "Neha Agarwal",
    city: "Jaipur",
    rating: 5,
    text: "Living in Jaipur, we see a lot of Pichwai everywhere, so I'm picky. Iragai's piece genuinely stood out, the silk base, the zardozi work, the proportions are spot on. You can tell real artisans worked on it."
  },
  {
    name: "Aditya Malhotra",
    city: "Gurgaon",
    rating: 5,
    text: "Found Iragai through Instagram and was a bit unsure since prices aren't listed. But the enquiry process was smooth, transparent, and the team shared everything I needed. Ended up buying two pieces for our farmhouse."
  }
];

// Reviews Section (Auto-scrolling Marquee)
const ReviewsSection = () => {
  // Duplicate list for seamless infinite loop
  const loopReviews = [...CUSTOMER_REVIEWS, ...CUSTOMER_REVIEWS];

  return (
    <section
      data-testid="reviews-section"
      className="py-24 bg-beige-50 overflow-hidden"
      aria-label="Customer reviews"
    >
      <div className="max-w-7xl mx-auto px-6 mb-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-iragai-gold block mb-4">
            Loved by Collectors
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl font-light text-iragai-dark mb-4">
            What Our Patrons Say
          </h2>
          <p className="font-body text-iragai-text max-w-2xl mx-auto">
            A few words from families who now have an Iragai Pichwai gracing their walls.
          </p>
        </motion.div>
      </div>

      <div className="reviews-marquee-mask">
        <div className="reviews-marquee-track" data-testid="reviews-marquee">
          {loopReviews.map((r, i) => (
            <article
              key={`${r.name}-${i}`}
              data-testid={`review-card-${i}`}
              className="mx-3 sm:mx-4 w-[260px] sm:w-[320px] shrink-0 bg-beige-100 border border-iragai-gold/15 p-5 sm:p-6 flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-center gap-1 mb-4" aria-label={`${r.rating} out of 5 stars`}>
                  {[0,1,2,3,4].map((s) => (
                    <Star
                      key={s}
                      className={`w-4 h-4 ${s < r.rating ? 'fill-iragai-gold text-iragai-gold' : 'text-iragai-gold/30'}`}
                    />
                  ))}
                </div>
                <p className="font-body text-iragai-text text-sm leading-relaxed mb-5 text-center">
                  {r.text}
                </p>
              </div>
              <div className="pt-4 border-t border-iragai-dark/10 text-center">
                <p className="font-heading text-iragai-dark text-base">{r.name}</p>
                <p className="font-body text-xs uppercase tracking-wider text-iragai-muted mt-1">{r.city}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// Home Page
const Home = () => {
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);

  usePageSEO({
    title: "Iragai — Sacred Pichwai Embroidery Art | Handcrafted Indian Wall Art",
    description: "Buy luxury handmade Pichwai embroidery artwork, Shrinathji embroidered art, and Radha Krishna wall art from Nathdwara at Iragai. Hand-embroidered with zardozi and beadwork. Worldwide shipping, custom sizes, certificate of authenticity. Enquire now.",
    path: "/",
    image: "https://customer-assets.emergentagent.com/job_iragai-gallery/artifacts/r4v3zz67_ChatGPT%20Image%20Jun%203%2C%202026%2C%2012_19_31%20PM.png",
    imageAlt: "Handmade Pichwai Shrinathji embroidery artwork in a luxury Indian living room - Iragai"
  });

  useEffect(() => {
    const fetchArtworks = async () => {
      try {
        const res = await axios.get(`${API}/artworks/featured`);
        setArtworks(res.data);
      } catch (e) {
        console.error("Error:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchArtworks();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(https://static.prod-images.emergentagent.com/jobs/bc1b4558-2cb0-40ca-a865-ce73f86a72e1/images/9bba9ee0d35f8fd0ec99e2e7c56cac14ad47b6a1a6913586fcac318f9e270191.png)`,
          }}
          role="img"
          aria-label="Pichwai Art Background featuring Lord Shrinathji"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-beige-50/90 via-beige-50/70 to-beige-50" />
        
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: "easeOut" }}
          className="relative z-10 text-center px-6 max-w-4xl"
        >
          <img 
            src={LOGO_URL} 
            alt="Iragai - Premium Pichwai Art Collection India" 
            className="h-20 w-auto mx-auto mb-6"
          />
          <span className="text-xs font-semibold uppercase tracking-[0.3em] text-iragai-gold mb-6 block">
            Sacred Art Collection
          </span>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light text-iragai-dark leading-tight mb-6">
            Where Devotion Meets<br />
            <span className="italic">Artistry</span>
          </h1>
          <p className="font-body text-iragai-text text-base sm:text-lg max-w-2xl mx-auto mb-10 leading-relaxed">
            Discover exquisite Pichwai masterpieces, each thread woven with centuries of tradition and divine inspiration.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/collection"
              data-testid="hero-cta"
              className="inline-flex items-center justify-center gap-3 bg-iragai-dark text-beige-50 px-8 py-4 font-body text-sm uppercase tracking-wider hover:bg-iragai-gold transition-colors duration-300"
            >
              Explore Collection
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-3 border border-iragai-gold text-iragai-dark px-8 py-4 font-body text-sm uppercase tracking-wider hover:bg-beige-100 transition-colors duration-300"
            >
              Enquire Now
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Featured Artworks */}
      <section className="pt-10 pb-16 sm:py-24 px-6 bg-beige-50">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8 sm:mb-16"
          >
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-iragai-gold block mb-4">
              Curated Selection
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl font-light text-iragai-dark">
              Featured Masterpieces
            </h2>
          </motion.div>

          {loading ? (
            <div className="flex justify-center">
              <div className="w-12 h-12 border-2 border-iragai-gold border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-8">
              {artworks.slice(0, 4).map((artwork, index) => (
                <motion.div
                  key={artwork.id}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <ArtworkCard artwork={artwork} hideEnquire />
                </motion.div>
              ))}
            </div>
          )}

          <div className="text-center mt-16">
            <Link
              to="/collection"
              data-testid="view-all-cta"
              className="inline-flex items-center gap-2 border border-iragai-gold text-iragai-dark px-8 py-3 font-body text-sm uppercase tracking-wider hover:bg-beige-100 transition-colors duration-300"
            >
              View All Artworks
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Brand Story Section */}
      <section className="pt-24 pb-8 sm:pb-12 px-6 bg-beige-100">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <img
              src="https://customer-assets.emergentagent.com/job_iragai-gallery/artifacts/r4v3zz67_ChatGPT%20Image%20Jun%203%2C%202026%2C%2012_19_31%20PM.png"
              alt="Pichwai Shrinathji Art in Luxury Indian Interior - Traditional Wall Art Decor"
              className="w-full aspect-square object-cover"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-iragai-gold block mb-4">
              Our Heritage
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl font-light text-iragai-dark mb-6">
              A Legacy of Sacred Artistry
            </h2>
            <p className="font-body text-iragai-text leading-relaxed mb-6">
              Iragai brings you the timeless beauty of Pichwai art, an ancient tradition born in the temple towns of Rajasthan. Each piece in our collection is meticulously handcrafted using traditional techniques like intricate beadwork, zardozi embroidery, and precious embellishments.
            </p>
            <p className="font-body text-iragai-text leading-relaxed mb-8">
              Our artisans are keepers of this sacred craft, transforming silk and thread into divine narratives that have adorned temples and palaces for centuries.
            </p>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 text-iragai-dark font-body text-sm uppercase tracking-wider hover:text-iragai-gold transition-colors"
            >
              Learn Our Story
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Divine Narratives - Reversed Layout */}
      <section className="pt-8 sm:pt-12 pb-24 px-6 bg-beige-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="order-2 lg:order-1"
          >
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-iragai-gold block mb-4">
              Divine Narratives
            </span>
            <h2 className="font-heading text-4xl sm:text-5xl font-light text-iragai-dark mb-6">
              The Eternal Love of Radha &amp; Krishna
            </h2>
            <p className="font-body text-iragai-text leading-relaxed mb-6">
              At the heart of every Pichwai embroidery artwork lies a story, and few are as soul-stirring as the divine union of <strong className="font-normal text-iragai-dark">Radha and Krishna</strong> beneath the sacred Kadamba tree. Stitched in deep vermilion reds, antique golds and lapis blues, this handmade masterpiece is a living tribute to centuries-old <strong className="font-normal text-iragai-dark">Indian temple art</strong> from Nathdwara.
            </p>
            <p className="font-body text-iragai-text leading-relaxed mb-8">
              Every leaf, every peacock, every gentle curve of the lovers&apos; embrace is hand-embroidered by master artisans using zardozi, fine beadwork, gold thread, and sequins on silk. More than a wall art, it is a doorway into a luxury Indian home, a piece that elevates your living room, drawing room, or pooja space into something timeless and sacred.
            </p>
            <Link
              to="/collection"
              className="inline-flex items-center gap-2 text-iragai-dark font-body text-sm uppercase tracking-wider hover:text-iragai-gold transition-colors"
            >
              Explore the Collection
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="order-1 lg:order-2"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_iragai-gallery/artifacts/irq03ql4_ChatGPT%20Image%20May%2025%2C%202026%2C%2011_23_58%20AM.png"
              alt="Radha Krishna Pichwai Painting under Kadamba Tree - Handcrafted Indian Wall Art in Luxury Living Room"
              loading="lazy"
              className="w-full aspect-square object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* Customer Reviews */}
      <ReviewsSection />

      {/* CTA Section */}
      <section className="py-20 px-6 bg-iragai-dark">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Sparkles className="w-8 h-8 text-iragai-gold mx-auto mb-6" />
            <h2 className="font-heading text-3xl sm:text-4xl font-light text-beige-50 mb-4">
              Interested in a Masterpiece?
            </h2>
            <p className="font-body text-beige-200 mb-8 max-w-xl mx-auto">
              Each artwork is unique and created with exceptional craftsmanship. Contact us to learn more about pricing and availability.
            </p>
            <Link
              to="/contact"
              data-testid="cta-enquire"
              className="inline-flex items-center gap-3 bg-iragai-gold text-iragai-dark px-8 py-4 font-body text-sm uppercase tracking-wider hover:bg-beige-50 transition-colors duration-300"
            >
              Enquire Now
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

// Artwork Card Component
const ArtworkCard = ({ artwork, hideEnquire = false }) => {
  const navigate = useNavigate();

  // Generate SEO-friendly alt text
  const getAltText = () => {
    return `${artwork.title} - Handcrafted ${artwork.category} with ${artwork.medium}`;
  };

  return (
    <div 
      data-testid={`artwork-card-${artwork.id}`}
      className="group artwork-card cursor-pointer"
      onClick={() => navigate(`/artwork/${artwork.id}`)}
    >
      <div className="relative overflow-hidden mb-4 bg-beige-100">
        <img
          src={artwork.image_url}
          alt={getAltText()}
          className="artwork-image w-full aspect-square object-cover"
        />
        <div className="absolute inset-0 bg-iragai-dark/0 group-hover:bg-iragai-dark/10 transition-colors duration-500" />
        {!hideEnquire && (
          <button
            data-testid={`enquire-btn-${artwork.id}`}
            onClick={(e) => {
              e.stopPropagation();
              navigate('/contact', { state: { artworkTitle: artwork.title, artworkId: artwork.id } });
            }}
            className="absolute bottom-4 left-4 right-4 bg-beige-50/95 backdrop-blur-sm text-iragai-dark py-3 font-body text-sm uppercase tracking-wider opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0 hover:bg-iragai-gold hover:text-beige-50"
          >
            Enquire Now
          </button>
        )}
      </div>
      <h3 className="font-heading text-base sm:text-xl text-iragai-dark mb-1">{artwork.title}</h3>
      <p className="font-body text-iragai-muted text-xs sm:text-sm">{artwork.category}</p>
    </div>
  );
};

// Collection Page
const Collection = () => {
  const [artworks, setArtworks] = useState([]);
  const [loading, setLoading] = useState(true);

  usePageSEO({
    title: "Collection | Iragai — Handmade Pichwai Embroidery Artwork",
    description: "Explore Iragai's curated collection of handmade Pichwai embroidery artwork, Shrinathji embroidered art, Radha Krishna wall art, and traditional hand-embroidered Indian masterpieces from Nathdwara. Custom sizes available.",
    path: "/collection"
  });

  useEffect(() => {
    const seedAndFetch = async () => {
      try {
        await axios.post(`${API}/seed-artworks`);
        const res = await axios.get(`${API}/artworks`);
        setArtworks(res.data);
      } catch (e) {
        console.error("Error:", e);
      } finally {
        setLoading(false);
      }
    };
    seedAndFetch();
  }, []);

  return (
    <div data-testid="collection-page" className="pt-32 pb-24 px-6 min-h-screen bg-beige-50">
      <div className="max-w-7xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-iragai-gold block mb-4">
            The Gallery
          </span>
          <h1 className="font-heading text-5xl sm:text-6xl font-light text-iragai-dark mb-4">
            Our Collection
          </h1>
          <p className="font-body text-iragai-text max-w-2xl mx-auto">
            Browse our curated collection of handmade Pichwai embroidery artwork, Shrinathji embroidered wall art, Radha Krishna art, and traditional hand-embroidered Indian masterpieces from Nathdwara. Each piece is a unique expression of devotion, stitched by master artisans using zardozi, fine beadwork, gold thread, and sequins on silk.
          </p>
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-12 h-12 border-2 border-iragai-gold border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-10">
            {artworks.map((artwork, index) => (
              <motion.div
                key={artwork.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <ArtworkCard artwork={artwork} />
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Artwork Detail Page
const ArtworkDetail = () => {
  const { id } = useParams();
  const [artwork, setArtwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  usePageSEO({
    title: artwork ? `${artwork.title} | Iragai — Handcrafted Pichwai Embroidery Art` : "Pichwai Embroidery Artwork | Iragai",
    description: artwork ? `${artwork.title} — handmade Pichwai embroidery artwork by Iragai. ${(artwork.description || '').slice(0, 150)}` : "Handmade Pichwai embroidery artwork from Iragai. Enquire for pricing, sizes, and shipping.",
    path: `/artwork/${id}`,
    image: artwork ? artwork.image_url : undefined,
    imageAlt: artwork ? `${artwork.title} — Handmade Pichwai Embroidery Artwork by Iragai` : undefined,
    type: "product"
  });

  // Inject Product schema per artwork (no price, enquiry-based)
  useEffect(() => {
    if (!artwork) return;
    const productSchema = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": artwork.title,
      "description": artwork.description || `${artwork.title} — a handmade Pichwai embroidery artwork by Iragai, hand-embroidered by master artisans from Nathdwara using zardozi, fine beadwork, gold thread, and sequins on silk.`,
      "image": [artwork.image_url, ...(artwork.gallery_images || [])].filter(Boolean),
      "brand": { "@type": "Brand", "name": "Iragai" },
      "category": artwork.category || "Pichwai Embroidery Artwork",
      "material": artwork.medium || "Handmade zardozi and silk embroidery",
      "sku": artwork.id,
      "url": `https://www.iragai.in/artwork/${artwork.id}`,
      "offers": {
        "@type": "Offer",
        "availability": "https://schema.org/InStock",
        "priceCurrency": "INR",
        "priceSpecification": {
          "@type": "PriceSpecification",
          "priceCurrency": "INR",
          "valueAddedTaxIncluded": true
        },
        "seller": { "@type": "Organization", "name": "Iragai", "url": "https://www.iragai.in" },
        "url": `https://www.iragai.in/artwork/${artwork.id}`,
        "businessFunction": "http://purl.org/goodrelations/v1#Sell",
        "itemCondition": "https://schema.org/NewCondition"
      }
    };
    const existing = document.head.querySelector('script[data-iragai-product-schema]');
    if (existing) existing.remove();
    const tag = document.createElement('script');
    tag.type = 'application/ld+json';
    tag.setAttribute('data-iragai-product-schema', 'true');
    tag.text = JSON.stringify(productSchema);
    document.head.appendChild(tag);
    return () => {
      const el = document.head.querySelector('script[data-iragai-product-schema]');
      if (el) el.remove();
    };
  }, [artwork]);

  useEffect(() => {
    const fetchArtwork = async () => {
      try {
        const res = await axios.get(`${API}/artworks/${id}`);
        setArtwork(res.data);
      } catch (e) {
        console.error("Error:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchArtwork();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-beige-50">
        <div className="w-12 h-12 border-2 border-iragai-gold border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!artwork) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-beige-50">
        <div className="text-center">
          <h2 className="font-heading text-3xl text-iragai-dark mb-4">Artwork Not Found</h2>
          <Link to="/collection" className="text-iragai-gold hover:underline">Back to Collection</Link>
        </div>
      </div>
    );
  }

  // Use gallery_images if available, otherwise fallback to single image
  const galleryImages = artwork.gallery_images && artwork.gallery_images.length > 0 
    ? artwork.gallery_images 
    : [artwork.image_url];

  return (
    <div data-testid="artwork-detail-page" className="pt-24 pb-24 min-h-screen bg-beige-50">
      <div className="max-w-7xl mx-auto px-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-iragai-text hover:text-iragai-gold mb-8 font-body text-sm"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Collection
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Image Gallery */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <ImageGallery images={galleryImages} title={artwork.title} />
          </motion.div>

          {/* Details */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col justify-center"
          >
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-iragai-gold block mb-2">
              {artwork.category}
            </span>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-iragai-dark mb-6">
              {artwork.title}
            </h1>
            
            <p className="font-body text-iragai-text leading-relaxed mb-6">
              {artwork.description}
            </p>

            {/* Highlights / Bullet Points */}
            {artwork.highlights && artwork.highlights.length > 0 && (
              <div className="bg-beige-100 p-6 mb-6">
                <h3 className="font-heading text-lg text-iragai-dark mb-4">Highlights</h3>
                <ul className="space-y-3">
                  {artwork.highlights.map((highlight, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-iragai-gold flex-shrink-0 mt-0.5" />
                      <span className="font-body text-iragai-text">{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="border-t border-iragai-dark/10 pt-6 mb-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <span className="text-xs uppercase tracking-wider text-iragai-muted">Dimensions</span>
                  <p className="font-body text-iragai-dark mt-1">{artwork.dimensions}</p>
                </div>
                <div>
                  <span className="text-xs uppercase tracking-wider text-iragai-muted">Medium</span>
                  <p className="font-body text-iragai-dark mt-1">{artwork.medium}</p>
                </div>
              </div>
            </div>

            {/* Customization Banner */}
            <div className="bg-iragai-gold/10 border border-iragai-gold/30 p-4 mb-6">
              <div className="flex items-center gap-3">
                <Sparkles className="w-5 h-5 text-iragai-gold flex-shrink-0" />
                <div>
                  <p className="font-heading text-lg text-iragai-dark">Custom Sizes & Framing Available</p>
                  <p className="font-body text-sm text-iragai-text">Contact us for personalized dimensions and framing options</p>
                </div>
              </div>
            </div>

            <Link
              to="/contact"
              state={{ artworkId: artwork.id, artworkTitle: artwork.title }}
              data-testid="enquire-detail-btn"
              className="w-full bg-iragai-dark text-beige-50 py-4 font-body text-sm uppercase tracking-wider text-center hover:bg-iragai-gold transition-colors duration-300 flex items-center justify-center gap-3"
            >
              <Sparkles className="w-4 h-4" />
              Enquire About This Artwork
            </Link>

            <button
              className="flex items-center justify-center gap-2 mt-4 text-iragai-text hover:text-iragai-gold transition-colors font-body text-sm"
            >
              <Heart className="w-4 h-4" />
              Add to Wishlist
            </button>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

// About Page
const About = () => {
  usePageSEO({
    title: "About Iragai | Nathdwara Artisans & Handmade Pichwai Embroidery Heritage",
    description: "Learn about Iragai's mission to preserve handmade Pichwai embroidery from Nathdwara. Meet our master artisans, craftsmanship, and the heritage behind every hand-embroidered Indian artwork.",
    path: "/about"
  });
  return (
  <div data-testid="about-page" className="pt-32 pb-24 min-h-screen bg-beige-50">
    <div className="max-w-7xl mx-auto px-6">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-16"
      >
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-iragai-gold block mb-4">
          Our Story
        </span>
        <h1 className="font-heading text-5xl sm:text-6xl font-light text-iragai-dark">
          About Iragai
        </h1>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <img
            src="https://customer-assets.emergentagent.com/job_iragai-gallery/artifacts/ox4z37tj_Sukha_1_20a30326-1e5f-4208-b76f-ce07c3d7b604%20%281%29.jpg"
            alt="Sukha - Handcrafted Bejeweled Parrots with Red Gulmohar Flowers - Intricate Beadwork Embroidery Art"
            className="w-full h-[500px] object-contain bg-beige-100"
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="font-heading text-3xl sm:text-4xl font-light text-iragai-dark mb-6">
            The Art of Precious Bejeweled Embroidery
          </h2>
          <p className="font-body text-iragai-text leading-relaxed mb-6">
            At Iragai, we specialize in creating exquisite handcrafted artwork that combines traditional Indian embroidery techniques with contemporary aesthetics. Our pieces feature intricate beadwork, zardozi embroidery, and precious embellishments that transform silk into stunning works of art.
          </p>
          <p className="font-body text-iragai-text leading-relaxed mb-6">
            Each artwork is meticulously hand-embroidered by master artisans who have inherited these sacred techniques through generations. From traditional Pichwai embroidery artwork depicting Lord Shrinathji to contemporary nature-inspired pieces, every creation tells a story of devotion, stitched in zardozi, beadwork, gold thread, and sequins.
          </p>
          <p className="font-body text-iragai-text leading-relaxed">
            We offer customization options for all our pieces, allowing you to choose sizes, framing, and even commission bespoke artworks tailored to your space and preferences.
          </p>
        </motion.div>
      </div>

      <div className="bg-beige-100 p-12 lg:p-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
          <div>
            <span className="font-heading text-5xl text-iragai-gold">400+</span>
            <p className="font-body text-iragai-text mt-2 uppercase tracking-wider text-sm">Years of Tradition</p>
          </div>
          <div>
            <span className="font-heading text-5xl text-iragai-gold">100%</span>
            <p className="font-body text-iragai-text mt-2 uppercase tracking-wider text-sm">Handcrafted</p>
          </div>
          <div>
            <span className="font-heading text-5xl text-iragai-gold">12+</span>
            <p className="font-body text-iragai-text mt-2 uppercase tracking-wider text-sm">Master Artisans</p>
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};

// Blog List Page (/blog)
const BlogList = () => {
  usePageSEO({
    title: "Journal | Iragai — Pichwai Embroidery Stories & Buyer Guides",
    description: "Explore the Iragai Journal for in-depth articles on handmade Pichwai embroidery artwork, Shrinathji embroidery, Nathdwara heritage, and expert buyer guides for traditional Indian hand-embroidered wall art.",
    path: "/blog"
  });

  return (
    <div data-testid="blog-list-page" className="pt-32 pb-24 px-6 min-h-screen bg-beige-50">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-iragai-gold block mb-4">
            The Iragai Journal
          </span>
          <h1 className="font-heading text-5xl sm:text-6xl font-light text-iragai-dark mb-4">
            Stories From Nathdwara
          </h1>
          <p className="font-body text-iragai-text max-w-2xl mx-auto">
            Long-form essays on handmade Pichwai embroidery, Shrinathji embroidered art, sacred Indian craftsmanship, and how to live with hand-embroidered heritage in a modern home.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {BLOG_POSTS.map((post, index) => (
            <motion.article
              key={post.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group"
              data-testid={`blog-card-${post.slug}`}
            >
              <Link to={`/blog/${post.slug}`} className="block">
                <div className="overflow-hidden bg-beige-100 mb-5">
                  <img
                    src={post.image}
                    alt={post.imageAlt}
                    loading="lazy"
                    className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="flex items-center gap-4 text-xs uppercase tracking-wider text-iragai-muted mb-3">
                  <span className="text-iragai-gold font-semibold">{post.category}</span>
                  <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" />{post.dateLabel}</span>
                  <span className="flex items-center gap-1.5"><Clock className="w-3 h-3" />{post.readTime}</span>
                </div>
                <h2 className="font-heading text-2xl sm:text-3xl font-light text-iragai-dark mb-3 group-hover:text-iragai-gold transition-colors">
                  {post.title}
                </h2>
                <p className="font-body text-iragai-text text-sm leading-relaxed mb-4">
                  {post.excerpt}
                </p>
                <span className="inline-flex items-center gap-2 text-iragai-dark font-body text-sm uppercase tracking-wider group-hover:text-iragai-gold transition-colors">
                  Read Article <ArrowRight className="w-4 h-4" />
                </span>
              </Link>
            </motion.article>
          ))}
        </div>
      </div>
    </div>
  );
};

// Blog Detail Page (/blog/:slug)
const BlogDetail = () => {
  const { slug } = useParams();
  const post = getBlogBySlug(slug);

  usePageSEO({
    title: post ? `${post.title} | Iragai Journal` : "Iragai Journal",
    description: post ? post.metaDescription : "Iragai Journal article on handmade Pichwai embroidery artwork and Indian heritage.",
    path: `/blog/${slug}`,
    image: post ? post.image : undefined,
    imageAlt: post ? post.imageAlt : undefined,
    type: "article"
  });

  useEffect(() => {
    if (post) {
      // Inject Article schema for this post
      const schema = {
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": post.title,
        "image": [post.image],
        "datePublished": post.date,
        "dateModified": post.date,
        "author": { "@type": "Organization", "name": "Iragai", "url": "https://www.iragai.in" },
        "publisher": {
          "@type": "Organization",
          "name": "Iragai",
          "logo": { "@type": "ImageObject", "url": "https://customer-assets.emergentagent.com/job_iragai-gallery/artifacts/zmnptker_IRAGAI_LOGO-removebg-preview.png" }
        },
        "description": post.metaDescription,
        "keywords": post.metaKeywords,
        "mainEntityOfPage": { "@type": "WebPage", "@id": `https://www.iragai.in/blog/${post.slug}` }
      };
      const existing = document.head.querySelector('script[data-iragai-article-schema]');
      if (existing) existing.remove();
      const tag = document.createElement('script');
      tag.type = 'application/ld+json';
      tag.setAttribute('data-iragai-article-schema', 'true');
      tag.text = JSON.stringify(schema);
      document.head.appendChild(tag);

      // Per-page meta keywords
      let kw = document.head.querySelector('meta[name="keywords"]');
      if (kw) kw.setAttribute('content', post.metaKeywords);
    }
    return () => {
      const existing = document.head.querySelector('script[data-iragai-article-schema]');
      if (existing) existing.remove();
    };
  }, [post]);

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-beige-50 px-6 text-center">
        <h1 className="font-heading text-4xl text-iragai-dark mb-4">Article Not Found</h1>
        <Link to="/blog" className="text-iragai-gold hover:underline">Back to Journal</Link>
      </div>
    );
  }

  const otherPosts = BLOG_POSTS.filter(p => p.slug !== post.slug).slice(0, 2);

  return (
    <article data-testid="blog-detail-page" className="pt-32 pb-24 min-h-screen bg-beige-50">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Link to="/blog" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-iragai-muted hover:text-iragai-gold mb-8">
            <ChevronLeft className="w-4 h-4" /> Back to Journal
          </Link>

          <div className="flex items-center gap-4 text-xs uppercase tracking-wider text-iragai-muted mb-5">
            <span className="text-iragai-gold font-semibold">{post.category}</span>
            <span className="flex items-center gap-1.5"><Calendar className="w-3 h-3" />{post.dateLabel}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-3 h-3" />{post.readTime}</span>
          </div>

          <h1 className="font-heading text-4xl sm:text-5xl font-light text-iragai-dark leading-tight mb-6">
            {post.title}
          </h1>

          <p className="font-body text-iragai-text text-lg leading-relaxed mb-10 italic">
            {post.excerpt}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mb-12 flex justify-center"
        >
          <img
            src={post.image}
            alt={post.imageAlt}
            className="mx-auto max-w-lg w-full aspect-square object-cover shadow-lg"
          />
        </motion.div>

        <div className="prose-iragai space-y-10">
          {post.sections.map((section, i) => (
            <motion.section
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="font-heading text-2xl sm:text-3xl font-light text-iragai-dark mb-4">
                {section.heading}
              </h2>
              <p className="font-body text-iragai-text leading-[1.85] text-base">
                {section.body}
              </p>
            </motion.section>
          ))}
        </div>

        {/* Tags */}
        <div className="flex flex-wrap items-center gap-3 mt-14 pt-8 border-t border-iragai-dark/10">
          <Tag className="w-4 h-4 text-iragai-gold" />
          {post.tags.map(t => (
            <span key={t} className="text-xs uppercase tracking-wider text-iragai-muted bg-beige-100 px-3 py-1.5">
              {t}
            </span>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-16 p-8 bg-iragai-dark text-beige-50 text-center">
          <Sparkles className="w-7 h-7 text-iragai-gold mx-auto mb-4" />
          <h3 className="font-heading text-2xl mb-3">Found a Pichwai You Love?</h3>
          <p className="font-body text-beige-200 mb-6 max-w-xl mx-auto text-sm">
            Browse Iragai&apos;s curated collection of handmade Pichwai embroidery artwork, Shrinathji embroidered art, and Radha Krishna wall art from Nathdwara.
          </p>
          <Link
            to="/collection"
            data-testid="blog-cta-collection"
            className="inline-flex items-center gap-3 bg-iragai-gold text-iragai-dark px-7 py-3 font-body text-sm uppercase tracking-wider hover:bg-beige-50 transition-colors duration-300"
          >
            View Collection <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Related Posts */}
        {otherPosts.length > 0 && (
          <div className="mt-20">
            <h3 className="font-heading text-2xl text-iragai-dark mb-8">Continue Reading</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              {otherPosts.map(p => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="group" data-testid={`related-blog-${p.slug}`}>
                  <div className="overflow-hidden bg-beige-100 mb-4">
                    <img src={p.image} alt={p.imageAlt} loading="lazy" className="w-full aspect-[4/3] object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <h4 className="font-heading text-lg text-iragai-dark group-hover:text-iragai-gold transition-colors">{p.title}</h4>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </article>
  );
};

// Generic Info Page (Custom Orders, Shipping, Refund, Care, FAQ)
const InfoPage = ({ pageKey }) => {
  const page = INFO_PAGES[pageKey];
  usePageSEO({
    title: page.title.replace(/&amp;/g, "&"),
    description: page.description,
    path: page.path
  });

  useEffect(() => {
    // Per-page meta keywords
    let kw = document.head.querySelector('meta[name="keywords"]');
    if (kw && page.keywords) kw.setAttribute('content', page.keywords);
  }, [page]);

  return (
    <div data-testid={`info-page-${pageKey}`} className="pt-32 pb-24 min-h-screen bg-beige-50">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-iragai-gold block mb-4">
            Iragai
          </span>
          <h1
            className="font-heading text-4xl sm:text-5xl font-light text-iragai-dark mb-4"
            dangerouslySetInnerHTML={{ __html: page.heading }}
          />
          <p className="font-body text-iragai-text max-w-2xl mx-auto">
            {page.subheading}
          </p>
        </motion.div>

        <div className="space-y-10">
          {page.sections.map((s, i) => (
            <motion.section
              key={i}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2
                className="font-heading text-2xl sm:text-3xl font-light text-iragai-dark mb-3"
                dangerouslySetInnerHTML={{ __html: s.h }}
              />
              <p className="font-body text-iragai-text leading-[1.85]">{s.p}</p>
            </motion.section>
          ))}
        </div>

        <div className="mt-16 p-8 bg-iragai-dark text-beige-50 text-center">
          <Sparkles className="w-7 h-7 text-iragai-gold mx-auto mb-4" />
          <h3 className="font-heading text-2xl mb-3">Have a Question?</h3>
          <p className="font-body text-beige-200 mb-6 max-w-xl mx-auto text-sm">
            Our gallery team responds to every enquiry within 24 hours.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-3 bg-iragai-gold text-iragai-dark px-7 py-3 font-body text-sm uppercase tracking-wider hover:bg-beige-50 transition-colors duration-300"
          >
            Enquire Now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

// Contact Page
const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  usePageSEO({
    title: "Contact Iragai | Enquire for Handmade Pichwai Embroidery Artwork",
    description: "Contact Iragai to enquire about handmade Pichwai embroidery artwork, Shrinathji embroidered art, custom sizes, framing, and worldwide shipping. Email iragai.help@gmail.com or call +91 8779486687.",
    path: "/contact"
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, {
        ...formData,
        artwork_ids: []
      });
      toast.success("Inquiry submitted successfully! We'll get back to you soon.");
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error("Failed to submit inquiry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-32 pb-24 min-h-screen bg-beige-50">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-iragai-gold block mb-4">
            Get in Touch
          </span>
          <h1 className="font-heading text-5xl sm:text-6xl font-light text-iragai-dark mb-4">
            Enquire Now
          </h1>
          <p className="font-body text-iragai-text max-w-2xl mx-auto">
            Interested in our artworks? Send us your enquiry and our team will get back to you with pricing and availability.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
              <div>
                <label className="block text-xs uppercase tracking-wider text-iragai-muted mb-2">Name *</label>
                <input
                  type="text"
                  required
                  data-testid="contact-name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 bg-beige-100 border border-iragai-dark/10 focus:border-iragai-gold focus:outline-none font-body"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-iragai-muted mb-2">Email *</label>
                <input
                  type="email"
                  required
                  data-testid="contact-email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 bg-beige-100 border border-iragai-dark/10 focus:border-iragai-gold focus:outline-none font-body"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-iragai-muted mb-2">Phone</label>
                <input
                  type="tel"
                  data-testid="contact-phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-3 bg-beige-100 border border-iragai-dark/10 focus:border-iragai-gold focus:outline-none font-body"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-iragai-muted mb-2">Message *</label>
                <textarea
                  required
                  rows={5}
                  data-testid="contact-message"
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder="I'm interested in learning more about your artworks..."
                  className="w-full px-4 py-3 bg-beige-100 border border-iragai-dark/10 focus:border-iragai-gold focus:outline-none font-body resize-none"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="w-full bg-iragai-dark text-beige-50 py-4 font-body text-sm uppercase tracking-wider hover:bg-iragai-gold transition-colors duration-300 disabled:opacity-50 flex items-center justify-center gap-3"
              >
                {submitting ? 'Sending...' : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Send Enquiry
                  </>
                )}
              </button>
            </form>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            <div className="p-8 bg-beige-100">
              <h3 className="font-heading text-2xl text-iragai-dark mb-6">Contact Us</h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <MapPin className="w-5 h-5 text-iragai-gold flex-shrink-0 mt-1" />
                  <p className="font-body text-iragai-text">
                    Mumbai, India
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <Phone className="w-5 h-5 text-iragai-gold flex-shrink-0" />
                  <a href="tel:+918779486687" className="font-body text-iragai-text hover:text-iragai-gold transition-colors">
                    +91 8779486687
                  </a>
                </div>
                <div className="flex items-center gap-4">
                  <Mail className="w-5 h-5 text-iragai-gold flex-shrink-0" />
                  <a href="mailto:iragai.help@gmail.com" className="font-body text-iragai-text hover:text-iragai-gold transition-colors">
                    iragai.help@gmail.com
                  </a>
                </div>
                <div className="flex items-start gap-4 pt-4 border-t border-iragai-dark/10">
                  <FileText className="w-5 h-5 text-iragai-gold flex-shrink-0 mt-1" />
                  <div>
                    <p className="text-xs uppercase tracking-wider text-iragai-muted mb-1">GSTIN</p>
                    <p data-testid="contact-gstin" className="font-body text-iragai-text tracking-wide">
                      27AAICI1159B1ZU
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-8 bg-iragai-dark text-beige-50">
              <h3 className="font-heading text-2xl mb-4">Private Viewings</h3>
              <p className="font-body text-beige-200 leading-relaxed">
                For collectors interested in exclusive pieces or commissioned works, we offer private viewings by appointment. Contact us to schedule your visit.
              </p>
            </div>

            <div className="p-8 border border-iragai-gold/30">
              <h3 className="font-heading text-2xl text-iragai-dark mb-4">Why Enquire?</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-iragai-gold flex-shrink-0 mt-0.5" />
                  <span className="font-body text-iragai-text">Personalized pricing based on artwork</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-iragai-gold flex-shrink-0 mt-0.5" />
                  <span className="font-body text-iragai-text">Custom framing options available</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-iragai-gold flex-shrink-0 mt-0.5" />
                  <span className="font-body text-iragai-text">Worldwide shipping & insurance</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="w-5 h-5 text-iragai-gold flex-shrink-0 mt-0.5" />
                  <span className="font-body text-iragai-text">Certificate of authenticity included</span>
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

// Main App
function App() {
  return (
    <div className="App bg-beige-50 min-h-screen">
      <BrowserRouter>
        <ScrollToTop />
        <Header />
        <Toaster position="top-right" richColors />
        <main>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/collection" element={<Collection />} />
            <Route path="/artwork/:id" element={<ArtworkDetail />} />
            <Route path="/blog" element={<BlogList />} />
            <Route path="/blog/:slug" element={<BlogDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/custom-orders" element={<InfoPage pageKey="custom-orders" />} />
            <Route path="/shipping" element={<InfoPage pageKey="shipping" />} />
            <Route path="/refund-policy" element={<InfoPage pageKey="refund-policy" />} />
            <Route path="/care-guide" element={<InfoPage pageKey="care-guide" />} />
            <Route path="/faq" element={<InfoPage pageKey="faq" />} />
            <Route path="/contact" element={<Contact />} />
          </Routes>
        </main>
        <Footer />
      </BrowserRouter>
    </div>
  );
}

export default App;
